import React, { Fragment, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";

import useScrollPositionThrottled from "../../hooks/useScrollPositionThrottled";
import { retrieveFeedPosts } from "../../services/feedServices";
import { normalizeMediaType } from "../../utils/media";

import MobileHeader from "../Header/MobileHeader/MobileHeader";
import ImageGrid from "../ImageGrid/ImageGrid";
import PreviewImage from "../PreviewImage/PreviewImage";
import SkeletonLoader from "../SkeletonLoader/SkeletonLoader";

const FollowingPosts = ({ token, showAlert }) => {
  const history = useHistory();
  const [state, setState] = useState({
    posts: [],
    fetching: false,
    hasMore: true,
    initialized: false,
  });

  const openPost = (postId) => {
    if (!postId) {
      return;
    }
    history.push(`/post/${postId}`);
  };

  const loadPosts = useCallback(async (offset = 0) => {
    if (!token) {
      return;
    }

    try {
      setState((previous) => ({ ...previous, fetching: true }));
      const response = await retrieveFeedPosts(token, offset);

      setState((previous) => ({
        posts: offset > 0 ? [...previous.posts, ...response] : [...response],
        fetching: false,
        hasMore: response.length > 0,
        initialized: true,
      }));
    } catch (err) {
      const message = err?.message || "Unable to load posts.";
      showAlert(message, () => loadPosts(offset));
      setState((previous) => ({
        ...previous,
        fetching: false,
        initialized: true,
      }));
    }
  }, [token, showAlert]);

  useEffect(() => {
    loadPosts(0);
  }, [loadPosts]);

  useScrollPositionThrottled(
    ({ atBottom }) => {
      if (atBottom && state.hasMore && !state.fetching) {
        loadPosts(state.posts.length);
      }
    },
    null,
    [state, loadPosts]
  );

  const renderSkeleton = (amount) => {
    const skeleton = [];
    for (let i = 0; i < amount; i += 1) {
      skeleton.push(
        <SkeletonLoader
          key={`skeleton-${i}`}
          style={{ minHeight: "30rem" }}
          animated
        />
      );
    }
    return skeleton;
  };

  const parseMetadata = (metadata) => {
    if (!metadata) {
      return null;
    }
    if (typeof metadata === 'object') {
      return metadata;
    }
    try {
      return JSON.parse(metadata);
    } catch (err) {
      return null;
    }
  };

  const getMediaForPost = (post) => {
    const mediaItem = post.mediaItems && post.mediaItems[0];
    const directUrl = post.mediaUrl || post.image;
    const mediaUrl = mediaItem?.mediaUrl || directUrl;
    const thumbnail =
      mediaItem?.thumbnailUrl || post.thumbnail || mediaUrl || null;
    const metadata = parseMetadata(mediaItem?.metadata || post.metadata);
    const mediaType = normalizeMediaType(
      mediaItem?.mediaType || post.mediaType,
      mediaUrl || thumbnail
    );
    const filter = mediaItem?.filter || post.filter || '';
    return { mediaType, mediaUrl, thumbnail, filter, metadata };
  };

  const postsWithMedia = state.posts
    .map((post) => ({ ...post, _media: getMediaForPost(post) }))
    .filter((post) => post._media.mediaUrl);

  return (
    <Fragment>
      <MobileHeader>
        <div></div>
        <h3 className="heading-3">Following</h3>
        <div></div>
      </MobileHeader>
      <ImageGrid>
        {postsWithMedia.map((post) => {
          const { mediaType, mediaUrl, thumbnail, filter, metadata } =
            post._media;
          const aggregatedReactions =
            (post.likeCount || 0) +
            (post.loveCount || 0) +
            (post.hahaCount || 0);
          const likes =
            aggregatedReactions > 0
              ? aggregatedReactions
              : typeof post.postVotesCount === "number"
              ? post.postVotesCount
              : Array.isArray(post.postVotes)
              ? post.postVotes.length
              : Number(post.postVotes) || 0;
          const commentTotal =
            typeof post.commentCount === "number"
              ? post.commentCount
              : post.commentData?.comments?.length || 0;

          return (
            <PreviewImage
              key={post._id || post.postId}
              image={post.image}
              thumbnail={thumbnail}
              mediaUrl={mediaUrl}
              mediaType={mediaType}
              metadata={metadata}
              likes={likes}
              comments={commentTotal}
              commentCount={commentTotal}
              filter={filter}
              onClick={() => openPost(post.postId || post._id)}
            />
          );
        })}
        {state.fetching && renderSkeleton(6)}
      </ImageGrid>
      {!state.fetching && state.initialized && postsWithMedia.length === 0 && (
        <p style={{ textAlign: "center", width: "100%", marginTop: "2rem" }}>
          No recent posts to show yet.
        </p>
      )}
    </Fragment>
  );
};

FollowingPosts.propTypes = {
  token: PropTypes.string,
  showAlert: PropTypes.func.isRequired,
};

export default FollowingPosts;
